import type { PulledMasterChange } from './pulledMaster.js';
import {
  acknowledgeAtomicRpcV1,
  enqueueAtomicRpcV1,
  findProductionDayReopenForScopeV1,
  type AtomicRpcHandle,
  type AtomicRpcLocalMaster,
  type AtomicRpcMasterEffect,
  type AtomicRpcRecord,
} from './atomicRpcOutbox.js';
import { applyPulledMasterToStockCommandCache } from './stockCommandSqlite.js';

export type ProductionPlanMealServiceV1 =
  | 'colazione'
  | 'pranzo'
  | 'cena';

export interface ProductionPlanReplaceDraftItemV1 {
  recipe_id: string;
  output_name: string;
  category: 'PRIMI' | 'SECONDI' | 'CONTORNI' | 'FRUTTA' | 'ALTRO' | null;
  planned_portions: number;
  diet_code: string | null;
}

export interface ProductionPlanReplaceItemV1
  extends ProductionPlanReplaceDraftItemV1 {
  plan_item_id: string;
  production_id: string;
  reuse_preprint: boolean;
  trace_token: string;
}

export interface ProductionPlanReplaceRequestV1
  extends Record<string, unknown> {
  schema: 'trackeat.production-plan-replace';
  version: 1;
  request_id: string;
  tenant_id: string;
  business_id: string;
  service_date: string;
  meal_service: ProductionPlanMealServiceV1;
  items: ProductionPlanReplaceItemV1[];
}

export interface QueueProductionPlanReplacementInput {
  tenantId: string;
  businessId: string;
  actorUserId: string;
  serviceDate: string;
  mealService: ProductionPlanMealServiceV1;
  items: readonly ProductionPlanReplaceDraftItemV1[];
  requestId?: string;
  now?: string;
}

interface ProductionPlanReplaceResultV1 extends Record<string, unknown> {
  schema: 'trackeat.production-plan-replace-result';
  version: 1;
  request_id: string;
  request_digest: string;
  tenant_id: string;
  business_id: string;
  service_date: string;
  meal_service: ProductionPlanMealServiceV1;
  archived_plan_items: Record<string, unknown>[];
  archived_productions: Record<string, unknown>[];
  plan_items: Record<string, unknown>[];
  productions: Record<string, unknown>[];
  replayed: boolean;
}

function randomTraceToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes)
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

function serviceDateInRome(isoTimestamp: unknown): string | null {
  if (typeof isoTimestamp !== 'string') return null;
  const date = new Date(isoTimestamp);
  if (!Number.isFinite(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const byType = new Map(parts.map((part) => [part.type, part.value]));
  const year = byType.get('year');
  const month = byType.get('month');
  const day = byType.get('day');
  return year && month && day ? `${year}-${month}-${day}` : null;
}

function isEmptyArray(value: unknown): boolean {
  return Array.isArray(value) && value.length === 0;
}

function isReusablePreprint(
  master: AtomicRpcLocalMaster,
  input: {
    tenantId: string;
    businessId: string;
    serviceDate: string;
    mealService: ProductionPlanMealServiceV1;
    recipeId: string;
  },
): boolean {
  const row = master.row;
  return (
    !master.archived
    && master.tenant_id === input.tenantId
    && row.business_id === input.businessId
    && row.recipe_id === input.recipeId
    && row.meal_service === input.mealService
    && row.prestampa === true
    && row.source_production_plan_item_id == null
    && serviceDateInRome(row.produced_at) === input.serviceDate
    && isEmptyArray(row.inputs)
    && isEmptyArray(row.allergens_aggregated)
    && row.portions == null
    && row.ccp_core_temp_c == null
    && (row.ccp_notes == null || String(row.ccp_notes).trim() === '')
    && row.sample_taken_at == null
    && (row.notes == null || String(row.notes).trim() === '')
    && row.abbattuto !== true
    && row.consumo_crudo == null
    && row.abbattimento_t_min_c == null
    && row.abbattimento_durata_h == null
    && row.abbattimento_inizio_at == null
    && row.abbattimento_fine_at == null
    && typeof row.trace_token === 'string'
    && /^[0-9a-f]{32}$/.test(row.trace_token)
  );
}

function hasLocalOperationalFacts(row: Record<string, unknown>): boolean {
  return (
    !isEmptyArray(row.inputs)
    || !isEmptyArray(row.allergens_aggregated)
    || row.prestampa === true
    || row.portions != null
    || row.ccp_core_temp_c != null
    || (row.ccp_notes != null && String(row.ccp_notes).trim() !== '')
    || row.sample_taken_at != null
    || (row.notes != null && String(row.notes).trim() !== '')
    || row.shelf_life_minutes != null
    || row.expires_at != null
    || row.abbattuto === true
    || row.consumo_crudo != null
    || row.abbattimento_t_min_c != null
    || row.abbattimento_durata_h != null
    || row.abbattimento_inizio_at != null
    || row.abbattimento_fine_at != null
    || (row.menu_label != null && String(row.menu_label).trim() !== '')
  );
}

function requireUuid(value: string, field: string): void {
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      .test(value)
  ) {
    throw new Error(`production_plan_replace_invalid_${field}`);
  }
}

function validateQueueInput(input: QueueProductionPlanReplacementInput): void {
  requireUuid(input.tenantId, 'tenant_id');
  requireUuid(input.businessId, 'business_id');
  requireUuid(input.actorUserId, 'actor_user_id');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.serviceDate)) {
    throw new Error('production_plan_replace_invalid_service_date');
  }
  if (input.items.length < 1 || input.items.length > 500) {
    throw new Error('production_plan_replace_invalid_item_count');
  }
  const keys = new Set<string>();
  for (const item of input.items) {
    requireUuid(item.recipe_id, 'recipe_id');
    if (
      !item.output_name.trim()
      || item.output_name.trim().length > 200
      || !Number.isInteger(item.planned_portions)
      || item.planned_portions < 1
      || item.planned_portions > 10_000_000
    ) {
      throw new Error('production_plan_replace_invalid_item');
    }
    const key = `${item.recipe_id}:${item.diet_code ?? ''}`;
    if (keys.has(key)) {
      throw new Error('production_plan_replace_duplicate_recipe_diet');
    }
    keys.add(key);
  }
}

function localNoonUtc(serviceDate: string): string {
  return `${serviceDate}T12:00:00.000Z`;
}

export function productionPlanScopeKey(input: {
  businessId: string;
  serviceDate: string;
  mealService: ProductionPlanMealServiceV1;
}): string {
  return `${input.businessId}:${input.serviceDate}:${input.mealService}`;
}

export async function queueProductionPlanReplacementV1(
  input: QueueProductionPlanReplacementInput,
): Promise<AtomicRpcHandle> {
  validateQueueInput(input);
  const requestId = input.requestId ?? crypto.randomUUID();
  requireUuid(requestId, 'request_id');
  const now = input.now ?? new Date().toISOString();
  const initialItems: ProductionPlanReplaceItemV1[] = input.items.map((item) => ({
    ...item,
    output_name: item.output_name.trim(),
    diet_code: item.diet_code?.trim() || null,
    plan_item_id: crypto.randomUUID(),
    production_id: crypto.randomUUID(),
    reuse_preprint: false,
    trace_token: randomTraceToken(),
  }));
  const initialRequest: ProductionPlanReplaceRequestV1 = {
    schema: 'trackeat.production-plan-replace',
    version: 1,
    request_id: requestId,
    tenant_id: input.tenantId,
    business_id: input.businessId,
    service_date: input.serviceDate,
    meal_service: input.mealService,
    items: initialItems,
  };
  const reopenPrerequisite = await findProductionDayReopenForScopeV1({
    tenantId: input.tenantId,
    businessId: input.businessId,
    sheetDate: input.serviceDate,
  });
  if (reopenPrerequisite?.status === 'conflict') {
    throw new Error('production_day_reopen_conflict_requires_resolution');
  }

  const queued = await enqueueAtomicRpcV1({
    kind: 'production_plan_replace_v1',
    tenantId: input.tenantId,
    businessId: input.businessId,
    scopeKey: productionPlanScopeKey({
      businessId: input.businessId,
      serviceDate: input.serviceDate,
      mealService: input.mealService,
    }),
    request: initialRequest,
    prerequisiteRequestId: reopenPrerequisite?.request_id ?? null,
    now,
    optimisticMasters: async (context) => {
      const [plans, productions] = await Promise.all([
        context.listMasters('production_plan_items'),
        context.listMasters('productions'),
      ]);
      const oldPlans = plans.filter((master) => {
        const row = master.row;
        return (
          !master.archived
          && master.tenant_id === input.tenantId
          && row.business_id === input.businessId
          && row.service_date === input.serviceDate
          && row.meal_service === input.mealService
        );
      });
      if (oldPlans.some((master) => master.row.status !== 'pending')) {
        throw new Error('production_plan_replace_completed_plan_v1');
      }
      const oldPlanIds = new Set(oldPlans.map((master) => master.id));
      const oldProductions = productions.filter((master) => (
        !master.archived
        && master.tenant_id === input.tenantId
        && master.row.business_id === input.businessId
        && typeof master.row.source_production_plan_item_id === 'string'
        && oldPlanIds.has(master.row.source_production_plan_item_id)
      ));
      if (oldProductions.some((master) => hasLocalOperationalFacts(master.row))) {
        throw new Error('production_plan_replace_operational_production_v1');
      }

      const reusable = productions
        .filter((master) => !master.archived)
        .sort((left, right) => {
          const created = String(left.row.created_at ?? '')
            .localeCompare(String(right.row.created_at ?? ''));
          return created || left.id.localeCompare(right.id);
        });
      const usedPreprints = new Set<string>();
      const preparedItems = initialItems.map((item) => {
        if (item.diet_code !== null) return item;
        const candidate = reusable.find((master) => (
          !usedPreprints.has(master.id)
          && isReusablePreprint(master, {
            tenantId: input.tenantId,
            businessId: input.businessId,
            serviceDate: input.serviceDate,
            mealService: input.mealService,
            recipeId: item.recipe_id,
          })
        ));
        if (!candidate) return item;
        usedPreprints.add(candidate.id);
        return {
          ...item,
          production_id: candidate.id,
          reuse_preprint: true,
          trace_token: candidate.row.trace_token as string,
        };
      });

      const effects: AtomicRpcMasterEffect[] = [];
      for (const production of oldProductions) {
        effects.push({
          table: 'productions',
          id: production.id,
          op: 'archive',
          base_version: production.version,
          patch: { archived_at: now },
        });
      }
      for (const plan of oldPlans) {
        effects.push({
          table: 'production_plan_items',
          id: plan.id,
          op: 'archive',
          base_version: plan.version,
          patch: { archived_at: now },
        });
      }
      for (const item of preparedItems) {
        effects.push({
          table: 'production_plan_items',
          id: item.plan_item_id,
          op: 'insert',
          base_version: 0,
          patch: {
            id: item.plan_item_id,
            tenant_id: input.tenantId,
            business_id: input.businessId,
            service_date: input.serviceDate,
            meal_service: input.mealService,
            recipe_id: item.recipe_id,
            output_name: item.output_name,
            category: item.category,
            planned_portions: item.planned_portions,
            diet_code: item.diet_code,
            status: 'pending',
            source: 'aggregate',
            notes: null,
            done_at: null,
            done_by: null,
            created_at: now,
            archived_at: null,
            _atomic_rpc_request_id: requestId,
            _sync_state: 'pending',
          },
        });
        const preprint = item.reuse_preprint
          ? productions.find((master) => master.id === item.production_id)
          : null;
        effects.push({
          table: 'productions',
          id: item.production_id,
          op: item.reuse_preprint ? 'update' : 'insert',
          base_version: preprint?.version ?? 0,
          patch: {
            ...(item.reuse_preprint ? {} : {
              id: item.production_id,
              tenant_id: input.tenantId,
              business_id: input.businessId,
              created_by: input.actorUserId,
              produced_at: localNoonUtc(input.serviceDate),
              output_name: item.output_name,
              recipe_id: item.recipe_id,
              meal_service: input.mealService,
              portions: null,
              inputs: [],
              allergens_aggregated: [],
              trace_token: item.trace_token,
              created_at: now,
              archived_at: null,
            }),
            source_production_plan_item_id: item.plan_item_id,
            output_qty: item.planned_portions,
            output_uom: 'pasti',
            _atomic_rpc_request_id: requestId,
            _sync_state: 'pending',
          },
        });
      }

      return {
        request: { ...initialRequest, items: preparedItems },
        optimisticMasters: effects,
      };
    },
  });
  return queued.handle;
}

function requireResultObject(
  value: unknown,
  field: string,
): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`production_plan_replace_writeback_${field}_invalid`);
  }
  return value as Record<string, unknown>;
}

function requireResultRows(
  value: unknown,
  field: string,
): Record<string, unknown>[] {
  if (!Array.isArray(value)) {
    throw new Error(`production_plan_replace_writeback_${field}_invalid`);
  }
  return value.map((row) => requireResultObject(row, field));
}

export function validateProductionPlanReplaceResultV1(
  record: AtomicRpcRecord<ProductionPlanReplaceRequestV1>,
  value: unknown,
): ProductionPlanReplaceResultV1 {
  const result = requireResultObject(value, 'result');
  const request = record.request;
  if (
    result.schema !== 'trackeat.production-plan-replace-result'
    || result.version !== 1
    || result.request_id !== request.request_id
    || result.request_digest !== record.digest
    || result.tenant_id !== request.tenant_id
    || result.business_id !== request.business_id
    || result.service_date !== request.service_date
    || result.meal_service !== request.meal_service
    || typeof result.replayed !== 'boolean'
  ) {
    throw new Error('production_plan_replace_writeback_identity_mismatch');
  }
  const planItems = requireResultRows(result.plan_items, 'plan_items');
  const productions = requireResultRows(result.productions, 'productions');
  const archivedPlanItems = requireResultRows(
    result.archived_plan_items,
    'archived_plan_items',
  );
  const archivedProductions = requireResultRows(
    result.archived_productions,
    'archived_productions',
  );
  if (
    planItems.length !== request.items.length
    || productions.length !== request.items.length
  ) {
    throw new Error('production_plan_replace_writeback_cardinality_mismatch');
  }
  const plansById = new Map(planItems.map((row) => [row.id, row]));
  const productionsById = new Map(productions.map((row) => [row.id, row]));
  for (const item of request.items) {
    const plan = plansById.get(item.plan_item_id);
    const production = productionsById.get(item.production_id);
    if (
      !plan
      || plan.tenant_id !== request.tenant_id
      || plan.business_id !== request.business_id
      || plan.service_date !== request.service_date
      || plan.meal_service !== request.meal_service
      || plan.recipe_id !== item.recipe_id
      || plan.planned_portions !== item.planned_portions
      || !production
      || production.tenant_id !== request.tenant_id
      || production.business_id !== request.business_id
      || production.recipe_id !== item.recipe_id
      || production.source_production_plan_item_id !== item.plan_item_id
      || production.trace_token !== item.trace_token
    ) {
      throw new Error('production_plan_replace_writeback_semantic_mismatch');
    }
  }
  return {
    ...(result as ProductionPlanReplaceResultV1),
    plan_items: planItems,
    productions,
    archived_plan_items: archivedPlanItems,
    archived_productions: archivedProductions,
  };
}

function pulledMaster(
  table: string,
  row: Record<string, unknown>,
): PulledMasterChange {
  if (typeof row.id !== 'string') {
    throw new Error('production_plan_replace_writeback_master_id_missing');
  }
  const version = Number(row.version);
  if (!Number.isInteger(version) || version < 0) {
    throw new Error('production_plan_replace_writeback_master_version_invalid');
  }
  return {
    table,
    id: row.id,
    version,
    row,
  };
}

export async function acknowledgeProductionPlanReplaceResultV1(
  record: AtomicRpcRecord<ProductionPlanReplaceRequestV1>,
  value: unknown,
): Promise<void> {
  const result = validateProductionPlanReplaceResultV1(record, value);
  for (const row of result.archived_productions) {
    await applyPulledMasterToStockCommandCache(
      pulledMaster('productions', row),
      { excludeAtomicRequestId: record.request_id },
    );
  }
  for (const row of result.archived_plan_items) {
    await applyPulledMasterToStockCommandCache(
      pulledMaster('production_plan_items', row),
      { excludeAtomicRequestId: record.request_id },
    );
  }
  for (const row of result.plan_items) {
    await applyPulledMasterToStockCommandCache(
      pulledMaster('production_plan_items', row),
      { excludeAtomicRequestId: record.request_id },
    );
  }
  for (const row of result.productions) {
    await applyPulledMasterToStockCommandCache(
      pulledMaster('productions', row),
      { excludeAtomicRequestId: record.request_id },
    );
  }
  await acknowledgeAtomicRpcV1({
    request_id: record.request_id,
    kind: record.kind,
    tenant_id: record.tenant_id,
    scope_key: record.scope_key,
    digest: record.digest,
  });
}
